import React from 'react';
import PropTypes from 'prop-types';
import { useIsFeatureAllowedForICPAccount } from 'hooks/useIsFeatureAllowedForICPAccount';
import { nonProgrammaticActiveAccount } from 'modules/account-management/accountType';
import { CampaignCreativeType } from 'modules/campaigns/components/CampaignCreativeType/CampaignCreativeType';
import { ConversionConfiguration } from 'modules/campaigns/components/ConversionConfiguration/ConversionConfiguration';
import { ENTITY, FORM_MODES } from 'modules/campaigns/config';
import { useIsCreativeLibrary } from 'modules/campaigns/hooks/useIsCreativeLibrary';
import { AudienceTargetingSectionCampaignEditor } from 'modules/campaigns/modules/common-campaign-form/components/AudienceTargetingSection/AudienceTargetingSectionCampaignEditor';
import { useShouldRenderBidStrategyV2 } from 'modules/campaigns/modules/common-campaign-form/components/BidStrategyV2/hooks/useShouldRenderBidStrategyV2';
import { BiddingConversionGoalSection } from 'modules/campaigns/modules/common-campaign-form/components/BiddingConversionGoalSection';
import Branding from 'modules/campaigns/modules/common-campaign-form/components/Branding/Branding';
import BudgetSectionSponsored from 'modules/campaigns/modules/common-campaign-form/components/BudgetSectionSponsored/BudgetSectionSponsored';
import { CampaignGroupField } from 'modules/campaigns/modules/common-campaign-form/components/CampaignGroupField/CampaignGroupField';
import CampaignMarketingObjective from 'modules/campaigns/modules/common-campaign-form/components/CampaignMarketingObjective/CampaignMarketingObjective';
import { CampaignPerformanceRules } from 'modules/campaigns/modules/common-campaign-form/components/CampaignPerformanceRules/CampaignPerformanceRules';
import CommentsSection from 'modules/campaigns/modules/common-campaign-form/components/CommentsSection/CommentsSection';
import { ConversionRules } from 'modules/campaigns/modules/common-campaign-form/components/ConversionOptimization/ConversionRules';
import DuplicateCampaignSettingsSection from 'modules/campaigns/modules/common-campaign-form/components/DuplicateCampaignSettingsSection/DuplicateCampaignSettingsSection';
import { Funnel } from 'modules/campaigns/modules/common-campaign-form/components/Funnel/Funnel';
import ProfileSection from 'modules/campaigns/modules/common-campaign-form/components/ProfileSection/ProfileSection';
import { ReachEstimatorWidget } from 'modules/campaigns/modules/common-campaign-form/components/ReachEstimator';
import { useReachEstimatorEnabled } from 'modules/campaigns/modules/common-campaign-form/components/ReachEstimator/hooks/useReachEstimatorEnabled';
import ScheduleSection from 'modules/campaigns/modules/common-campaign-form/components/ScheduleSection/ScheduleSection';
import SetupSection from 'modules/campaigns/modules/common-campaign-form/components/SetupSection/SetupSection';
import TargetingSection from 'modules/campaigns/modules/common-campaign-form/components/TargetingSection/TargetingSection';
import TrackingCode from 'modules/campaigns/modules/common-campaign-form/components/TrackingCode/TrackingCode';
import TrackingSection from 'modules/campaigns/modules/common-campaign-form/components/TrackingSection/TrackingSection';
import {
    COMMON_FLAGS,
    FEATURE_FLAGS,
    useConfigMatch,
} from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';

const CampaignEditorContent = ({ shouldRenderAccountDropdown }) => {
    const { mode } = useFormDataContext();
    const isReachEstimatorEnabled = useReachEstimatorEnabled();
    const shouldRenderBidStrategyV2 = useShouldRenderBidStrategyV2();
    const isDisplayPermitted = useIsFeatureAllowedForICPAccount(FEATURE_FLAGS.DISPLAY_ITEM_ENABLED);
    const isInitiallyCollapsed = useConfigMatch(
        {},
        { [COMMON_FLAGS.CAMPAIGN_EDITOR_DRAWER_COLLAPSE_SECTIONS]: 'true' }
    );
    const { isCreativeLibraryEnabled, isCreativeLibraryCampaign } = useIsCreativeLibrary();

    const isLibraryCampaign = isCreativeLibraryEnabled && isCreativeLibraryCampaign;

    return (
        <>
            <div>
                {mode === FORM_MODES.DUPLICATE && <DuplicateCampaignSettingsSection />}
                <SetupSection
                    isAccountSelectionEnabled={shouldRenderAccountDropdown}
                    accountFilter={nonProgrammaticActiveAccount}
                >
                    <CampaignGroupField disabled={mode === FORM_MODES.EDIT} />
                    {!isCreativeLibraryEnabled && <Branding />}
                    <CampaignMarketingObjective />
                    {shouldRenderBidStrategyV2 && (
                        <>
                            <ConversionRules />
                            <ConversionConfiguration />
                            <Funnel />
                            {isDisplayPermitted && <CampaignCreativeType />}
                        </>
                    )}
                </SetupSection>
                {!shouldRenderBidStrategyV2 && (
                    <BiddingConversionGoalSection isInitiallyCollapsed={isInitiallyCollapsed} />
                )}
                <ScheduleSection isInitiallyCollapsed={isInitiallyCollapsed} />
                <TargetingSection isIncludeHighImpactTargeting shouldInitiallyCollapse={isInitiallyCollapsed} />
                <AudienceTargetingSectionCampaignEditor isInitiallyCollapsed={isInitiallyCollapsed} />
                <BudgetSectionSponsored isInitiallyCollapsed={isInitiallyCollapsed} />
                <CampaignPerformanceRules isInitiallyCollapsed={isInitiallyCollapsed} />
                <TrackingSection isInitiallyCollapsed={isInitiallyCollapsed}>
                    <TrackingCode />
                </TrackingSection>
                {!isLibraryCampaign && <ProfileSection isInitiallyCollapsed={isInitiallyCollapsed} />}
                <CommentsSection isInitiallyCollapsed={isInitiallyCollapsed} />
            </div>
            {isReachEstimatorEnabled && <ReachEstimatorWidget entityType={ENTITY.CAMPAIGN} />}
        </>
    );
};

CampaignEditorContent.propTypes = {
    currency: PropTypes.string,
    /* only for duplicate mode */
    account: PropTypes.object,
    onAccountChange: PropTypes.func,
    shouldRenderAccountDropdown: PropTypes.bool,
};

export default CampaignEditorContent;
